import React, { memo, useRef, useState, useEffect, useCallback } from "react";
import { Draggable } from "react-beautiful-dnd";
import {
  CheckCircle,
  ChevronDown,
  FileText,
  User,
  Tag,
  Speech,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { EnquiryTimelineIcon } from "../../pages/Enquiries/Enquirytimelinesidebar";
import { createPortal } from "react-dom";
import axiosInstance from "../../axiosInstance";

// The lightweight, initial-load endpoint only sends what a collapsed card
// needs (names/quantities, task counts — no ids/specs). These are the base
// paths for the endpoint that returns the FULL record for a single card;
// it's called once, lazily, the first time a card is expanded. Sales and
// sales-manager expose this under different paths, so pick the right one
// based on isTeamView (manager/team-lead) vs the individual sales view.
const CARD_DETAILS_ENDPOINT_BASE = "/enquiry-stage-history/employee";
const CARD_DETAILS_ENDPOINT_BASE_TEAM =
  "/enquiry-stage-history/manager/employee";

// Helper function to format date compactly
const formatCompactDateTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const timeStr = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (dateOnly.getTime() === today.getTime()) {
    return `Today ${timeStr}`;
  }

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (dateOnly.getTime() === yesterday.getTime()) {
    return `Yesterday ${timeStr}`;
  }

  return (
    date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    }) + ` ${timeStr}`
  );
};

const NotesTooltip = ({ notes }) => {
  const [coords, setCoords] = useState(null);
  const iconRef = useRef(null);

  if (!notes) return null;

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
    }
  };

  return (
    <>
      <span
        ref={iconRef}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setCoords(null)}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fef9c3",
            border: "1px solid #fde047",
            cursor: "default",
            flexShrink: 0,
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#ca8a04"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="8" y1="13" x2="16" y2="13" />
            <line x1="8" y1="17" x2="13" y2="17" />
          </svg>
        </span>
      </span>

      {/* Portal renders outside all overflow/transform parents */}
      {coords &&
        createPortal(
          <span
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              transform: "translateX(-50%)",
              background: "#1e1b0e",
              color: "#fef3c7",
              fontSize: "0.75rem",
              lineHeight: 1.5,
              padding: "7px 10px",
              borderRadius: 8,
              whiteSpace: "pre-wrap",
              maxWidth: 260,
              width: "max-content",
              zIndex: 99999,
              boxShadow: "0 4px 16px rgba(0,0,0,0.28)",
              pointerEvents: "none",
            }}
          >
            {notes}
            <span
              style={{
                position: "absolute",
                bottom: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                borderWidth: "0 5px 5px",
                borderStyle: "solid",
                borderColor: "transparent transparent #1e1b0e",
                display: "block",
                width: 0,
                height: 0,
              }}
            />
          </span>,
          document.body,
        )}
    </>
  );
};

const KanbanCard = memo(
  ({
    item,
    isChecklistComplete,
    index,
    onOpenModal,
    onQuickComplete,
    onProductClick,
    isExpanded,
    onOpenBOM,
    onToggleExpanded,
    isTeamView = false,
    onOpenMOM,
    onAddProducts,
    getApiEndpoint,
  }) => {
    const cardRef = useRef(null);
    const clientType = item.leadName ? "new-client" : "existing-client";
    const clientDisplayName = item.leadName || item.clientName;
    // WON/LOST are terminal stages (matches backend EnquiryStageType enum) —
    // once an enquiry lands there, product edits no longer apply.
    const isWonOrLostStage = ["WON", "LOST"].includes(
      item.stageDetails?.stageType?.toUpperCase(),
    );

    const [showAllProducts, setShowAllProducts] = useState(false);

    // ── Lazy full-detail fetch ────────────────────────────────────────────
    // `item` as received from the list view only carries what the card
    // needs collapsed. Once expanded, fetch the full record (real checklist
    // ids/completion state, product specs, remarks) and merge it in below.
    const [cardDetails, setCardDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const hasFetchedOnce = useRef(false);

    const fetchCardDetails = useCallback(async () => {
      if (!getApiEndpoint || !item?.id) return;
      setDetailsLoading(true);
      setDetailsError(null);
      try {
        const endpointBase = isTeamView
          ? CARD_DETAILS_ENDPOINT_BASE_TEAM
          : CARD_DETAILS_ENDPOINT_BASE;
        const response = await axiosInstance.get(
          getApiEndpoint(`${endpointBase}/${item.id}`),
        );
        const payload = response?.data?.data;
        const fullHistory =
          payload?.histories?.find((h) => h.id === item.id) ||
          payload?.histories?.[0] ||
          null;

        if (fullHistory) {
          hasFetchedOnce.current = true;
          setCardDetails({
            remarks: fullHistory.remarks || "",
            notes: fullHistory.notes,
            startedAt: fullHistory.startedAt,
            endedAt: fullHistory.endedAt,
            checkListHistories: (fullHistory.checkListHistories || []).map(
              (cl) => ({
                id: cl.id,
                checkListId: cl.checklistId,
                taskName: cl.checklistName || "Unnamed Task",
                remarks: cl.remarks || "",
                completed: !!cl.completed,
              }),
            ),
            productHistories: (fullHistory.productEnquiryHistories || []).map(
              (peh) => ({
                id: peh.id,
                productId: peh.productId,
                productName: peh.productName,
                skuCode: peh.skuCode,
                quantity: peh.quantity,
                fulfillmentStatus: peh.fulfillmentStatus || "PENDING",
                price: peh.price,
                specificationHistories: (peh.specificationHistories || []).map(
                  (sh) => ({
                    id: sh.id,
                    productSpecificationId: sh.productSpecificationId,
                    productSpecificationValueId: sh.productSpecificationValueId,
                    specsName: sh.specsName,
                    valueName: sh.valueName,
                  }),
                ),
              }),
            ),
          });
        }
      } catch (err) {
        console.error("Failed to fetch enquiry stage history details:", err);
        setDetailsError("Couldn't load full details. Showing summary.");
      } finally {
        setDetailsLoading(false);
      }
    }, [isTeamView, getApiEndpoint, item?.id]);

    useEffect(() => {
      if (isExpanded && !hasFetchedOnce.current && !detailsLoading) {
        fetchCardDetails();
      }
    }, [isExpanded, fetchCardDetails, detailsLoading]);

    // The parent (Enquiries/TeamEnquiry) refreshes `item.checkListHistories`
    // with real, authoritative data after every completion via its own
    // per-card endpoint. But this card's *own* fetchCardDetails() call above
    // fires independently on expand, and can resolve with a stale snapshot
    // around the same time — since cardDetails, once loaded, is preferred
    // over `item` unconditionally, that stale snapshot could silently win
    // and override the parent's fresher state. This kept a just-completed
    // task showing as incomplete until a second click's round-trip landed.
    // Re-sync cardDetails.checkListHistories whenever the parent supplies
    // real (non-placeholder) data so the parent's copy always wins once
    // it's available, regardless of fetchCardDetails' own timing.
    useEffect(() => {
      const realChecklist = (item.checkListHistories || []).filter(
        (t) =>
          !t.isPlaceholder && !t.id?.toString?.().startsWith("placeholder-"),
      );
      if (realChecklist.length > 0) {
        setCardDetails((prev) =>
          prev
            ? { ...prev, checkListHistories: realChecklist }
            : { checkListHistories: realChecklist },
        );
      }
    }, [item.checkListHistories]);

    // Prefer the fully-loaded detail once it arrives; fall back to whatever
    // the (lightweight) card already had so nothing ever looks empty.
    const effectiveChecklist =
      cardDetails?.checkListHistories?.length > 0
        ? cardDetails.checkListHistories
        : item.checkListHistories || [];
    const effectiveProducts =
      cardDetails?.productHistories?.length > 0
        ? cardDetails.productHistories
        : item.productHistories || [];
    const effectiveNotes = cardDetails?.notes ?? item.notes;
    const effectiveCompletedTasks = cardDetails
      ? effectiveChecklist.filter((t) => t.completed).length
      : item.completedTasks;
    const effectiveTotalTasks = cardDetails
      ? effectiveChecklist.length
      : item.totalTasks;
    const effectiveProgress =
      effectiveTotalTasks > 0
        ? (effectiveCompletedTasks / effectiveTotalTasks) * 100
        : 0;
    // Green light: nothing required (0/0) or everything done counts as ready
    // to advance. Mirrors the shared isChecklistComplete() rule used for the
    // actual drag validation, just computed off whichever counts (lightweight
    // list counts, or the fully-loaded detail once expanded) are on hand.
    const checklistReady =
      effectiveTotalTasks === 0 ||
      effectiveCompletedTasks === effectiveTotalTasks;

    // Calculate totals using productName from API
    const totalQuantity =
      effectiveProducts?.reduce((sum, p) => sum + (p.quantity || 0), 0) || 0;

    // Calculate Priority class safely
    const priorityLevel = (item.priority || "MEDIUM").toLowerCase();

    const handleQuickComplete = (taskId, itemId, e) => {
      e.stopPropagation();
      e.preventDefault();

      // Get the column and store scroll position
      const column = cardRef.current?.closest(".kanban-column-content");
      const scrollTop = column?.scrollTop || 0;

      // Optimistically flag the task complete in the fetched detail so the
      // expanded checklist reflects it immediately instead of waiting on a
      // re-expand to pick it up.
      setCardDetails((prev) =>
        prev
          ? {
              ...prev,
              checkListHistories: prev.checkListHistories.map((t) =>
                t.id === taskId ? { ...t, completed: true } : t,
              ),
            }
          : prev,
      );

      // Execute the completion
      onQuickComplete(taskId, itemId);

      // Restore scroll position multiple times to ensure it sticks
      const restoreScroll = () => {
        if (column) {
          column.scrollTop = scrollTop;
        }
      };

      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
    };

    const handleOpenModal = (taskId, isPlaceholder, e) => {
      e.stopPropagation();
      e.preventDefault();

      // Get the column and store scroll position
      const column = cardRef.current?.closest(".kanban-column-content");
      const scrollTop = column?.scrollTop || 0;

      onOpenModal(taskId, isPlaceholder);

      // Restore scroll position
      const restoreScroll = () => {
        if (column) {
          column.scrollTop = scrollTop;
        }
      };

      setTimeout(restoreScroll, 0);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
    };

    return (
      <Draggable draggableId={`item-${item.id}`} index={index}>
        {(provided, snapshot) => (
          <div
            ref={(el) => {
              provided.innerRef(el);
              cardRef.current = el;
            }}
            data-item-id={item.id}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`kanban-card-modern priority-${priorityLevel} ${
              snapshot.isDragging ? "dragging" : ""
            } ${
              checklistReady ? "checklist-ready" : "incomplete-checklist"
            } ${isExpanded ? "expanded" : ""}`}
            style={{
              ...provided.draggableProps.style,
              transform: snapshot.isDragging
                ? `${provided.draggableProps.style?.transform} rotate(2deg)`
                : provided.draggableProps.style?.transform,
            }}
          >
            {/* Card Header - Client Info WITH PRIORITY BADGE */}
            <div className="card-header-modern">
              <div className="client-badge-modern">
                <span
                  className={`client-name-badge ${
                    item.leadName ? "new-client" : "existing-client"
                  }`}
                >
                  <User size={14} />

                  {clientDisplayName}
                </span>
                <span className={`client-type-tag ${clientType}`}>
                  {item.leadName ? "New" : "Existing"}
                </span>
              </div>

              <div className="card-actions-header">
                {/* DUE DATE MOVED HERE */}
                {item.dueAt && !item.endedAt && (
                  <div className="info-row-compact date-info due">
                    <span>
                      {new Date(item.dueAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </span>
                  </div>
                )}
                {isTeamView && onOpenBOM && (
                  <button
                    className="edit-btn-modern bom-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenBOM(item);
                    }}
                  >
                    <FileText size={13} />
                    <span className="action-label">BOM</span>
                  </button>
                )}

                <EnquiryTimelineIcon
                  enquiryId={item.enquiryId}
                  getApiEndpoint={getApiEndpoint}
                />
                {onOpenMOM && (
                  <button
                    className="edit-btn-modern mom-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenMOM(item);
                    }}
                  >
                    <Speech size={13} />
                    <span className="action-label">MOM</span>
                  </button>
                )}
                {onAddProducts && !isWonOrLostStage && (
                  <button
                    className="edit-btn-modern add-products-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddProducts(item.id);
                    }}
                  >
                    <Tag size={13} />
                    <span className="action-label">Edit</span>
                  </button>
                )}
                <NotesTooltip notes={effectiveNotes} />

                <button
                  className={`expand-btn-modern ${isExpanded ? "expanded" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onToggleExpanded(item.id);
                  }}
                  type="button"
                >
                  <ChevronDown
                    size={12}
                    className={isExpanded ? "rotated" : ""}
                  />
                </button>
              </div>

              <div className="assigned-section">
                {isTeamView && item.assignedTo && (
                  <div className="assigned-user-badge">
                    <strong>Assigned To:</strong>
                    <span>{item.assignedTo}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="card-compact-info">
              {item.productHistories && item.productHistories.length > 0 && (
                <div
                  className="info-row-compact product-preview"
                  onMouseEnter={() => setShowAllProducts(true)}
                  onMouseLeave={() => setShowAllProducts(false)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllProducts((v) => !v);
                  }}
                  style={{ position: "relative", cursor: "pointer" }}
                >
                  <FileText size={11} />

                  <span className="product-name-preview">
                    {item.productHistories
                      .slice(0, 2)
                      .map((p) => p.productName || "Product")
                      .join(", ")}
                  </span>

                  {item.productHistories.length > 2 && (
                    <span className="product-name-preview"> … </span>
                  )}

                  {showAllProducts && item.productHistories.length > 2 && (
                    <div
                      className="products-preview-modern"
                      style={{
                        position: "absolute",
                        top: "120%",
                        left: 0,
                        zIndex: 50,
                        minWidth: "220px",
                      }}
                    >
                      {item.productHistories.map((p) => (
                        <div key={p.id} className="product-card-compact">
                          <div className="product-name-row">
                            <div className="product-name-left">
                              <FileText size={11} />
                              <span className="product-name-text">
                                {p.productName || `Product #${p.productId}`}
                              </span>
                            </div>
                            <div className="product-name-right">
                              <span className="qty-badge">×{p.quantity}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Notes moved OUTSIDE the product-preview div */}
              {item.notes && !isExpanded && (
                <div
                  className="info-row-compact notes-compact"
                  style={{
                    color: "#92400e",
                    fontSize: "0.73rem",
                    opacity: 0.85,
                    gap: 4,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span
                    style={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {item.notes}
                  </span>
                </div>
              )}
            </div>

            <div className="progress-expand-row">
              {effectiveTotalTasks > 0 && (
                <div
                  className={`progress-section-compact ${
                    checklistReady ? "checklist-ready" : ""
                  }`}
                >
                  <div className="progress-label-row">
                    <span className="progress-text">
                      {effectiveCompletedTasks}/{effectiveTotalTasks} tasks
                    </span>
                    <span className="progress-percent">
                      {Math.round(effectiveProgress)}%
                    </span>
                  </div>

                  <div className="progress-bar-compact">
                    <div
                      className={`progress-fill-compact ${
                        checklistReady ? "complete" : ""
                      }`}
                      style={{ width: `${effectiveProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="expanded-content-modern">
                {detailsLoading && (
                  <div
                    className="info-row-compact"
                    style={{ gap: 6, opacity: 0.75, fontSize: "0.75rem" }}
                  >
                    <Loader2 size={12} className="animate-spin" />
                    <span>Loading full details…</span>
                  </div>
                )}

                {detailsError && (
                  <div
                    className="info-row-compact"
                    style={{
                      gap: 6,
                      color: "#b45309",
                      fontSize: "0.75rem",
                    }}
                  >
                    <AlertCircle size={12} />
                    <span>{detailsError}</span>
                  </div>
                )}

                {/* Assigned To - team view only */}
                {isTeamView && item.assignedTo && (
                  <div className="additional-info-modern">
                    <div className="info-row same-row">
                      {/* Dates inline */}
                      {item.startedAt && (
                        <div className="date-badge started inline-date">
                          <Speech size={10} />
                          <span>{formatCompactDateTime(item.startedAt)}</span>
                        </div>
                      )}

                      {item.endedAt && (
                        <div className="date-badge completed inline-date">
                          <CheckCircle size={10} />
                          <span>{formatCompactDateTime(item.endedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Checklist */}
                {effectiveChecklist.length > 0 && (
                  <div className="checklist-expanded">
                    <div className="checklist-header-expanded">
                      <CheckCircle size={13} />
                      <span>Tasks</span>
                    </div>
                    <div className="checklist-items-modern">
                      {effectiveChecklist.map((task) => (
                        <div
                          key={task.id}
                          className={`task-item-modern ${
                            task.completed ? "completed" : ""
                          } ${task.isPlaceholder ? "placeholder" : ""}`}
                        >
                          <div
                            className="task-checkbox-modern clickable"
                            onClick={(e) => {
                              if (task.completed) return;
                              handleQuickComplete(task.id, item.id, e);
                            }}
                            role="checkbox"
                            aria-checked={task.completed}
                          >
                            {task.completed ? (
                              <CheckCircle
                                className="icon-completed"
                                size={14}
                              />
                            ) : (
                              <div className="checkbox-empty" />
                            )}
                          </div>

                          <span className="task-name-modern">
                            {task.taskName}
                            {task.isPlaceholder && (
                              <span className="placeholder-tag">
                                {" "}
                                (Not started)
                              </span>
                            )}
                          </span>
                          {!task.completed && (
                            <div className="task-actions-modern">
                              <button
                                onClick={(e) =>
                                  handleOpenModal(
                                    task.id,
                                    task.isPlaceholder,
                                    e,
                                  )
                                }
                                type="button"
                                title="Add remarks"
                              >
                                <FileText size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Products - Using productName from API */}
                {effectiveProducts && effectiveProducts.length > 0 && (
                  <div className="products-preview-modern">
                    <div className="product-header-compact">
                      <Tag size={10} />
                      <span>Products</span>
                    </div>
                    {effectiveProducts.slice(0, 3).map((product) => (
                      <div
                        key={product.id}
                        className="product-card-compact"
                        onClick={(e) => {
                          e.stopPropagation();
                          onProductClick && onProductClick(product);
                        }}
                      >
                        <div className="product-name-row">
                          <div className="product-name-left">
                            <FileText size={11} />
                            <span className="product-name-text">
                              {String(
                                product.productName ||
                                  `Product #${product.productId}`,
                              )}
                            </span>
                          </div>
                          <div className="product-name-right">
                            <span className="qty-badge">
                              ×{Number(product.quantity)}
                            </span>
                            {product.price > 0 && (
                              <span className="price-badge">
                                {Number(product.quantity)} × ₹
                                {Number(product.price).toLocaleString("en-IN")}
                              </span>
                            )}
                          </div>
                        </div>

                        {product.skuCode && (
                          <div className="sku-info">
                            <span className="sku-code">
                              SKU: {String(product.skuCode)}
                            </span>
                          </div>
                        )}

                        {Array.isArray(product.specificationHistories) &&
                          product.specificationHistories.length > 0 && (
                            <div className="specs-preview">
                              {product.specificationHistories
                                .slice(0, 2)
                                .map((spec, idx) => (
                                  <span key={idx} className="spec-chip">
                                    {String(
                                      typeof spec === "object"
                                        ? spec.specsName || spec.name || "Spec"
                                        : spec,
                                    ).substring(0, 8)}
                                  </span>
                                ))}
                              {product.specificationHistories.length > 2 && (
                                <span className="spec-more">
                                  +{product.specificationHistories.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                    ))}
                    {effectiveProducts.length > 3 && (
                      <div className="products-summary-compact">
                        <span>+{effectiveProducts.length - 3} more</span>
                        <span className="total-qty-compact">
                          Total: {totalQuantity} units
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes/Remarks */}
                {effectiveNotes && (
                  <div className="notes-section-modern">
                    <strong>Notes</strong>
                    <p>{effectiveNotes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Draggable>
    );
  },
);

KanbanCard.displayName = "KanbanCard";

export default KanbanCard;
